import { useMemo, useCallback } from "react"
import { chain, compact } from "lodash"
import PropTypes from "prop-types"
import { Paper, Box } from "@mui/material"
import Error from "components/error"
import Loading from "components/loading"
import { ERROR, LOADING } from "helpers/data-statuses"
import { getDriftStartAndEndDates } from "helpers/dates"
import { CONTENT_SET_METADATA } from "resources/content-sets"
import { useModelMetadata } from "resources/models"
import { useInstrumentsWithQueryOptions, getOrderedInstrumentColumns, useInstrumentsEarliestAndLatestDates } from "connected-components/dashboard/helpers"
import FactorsViewSelector from "connected-components/dashboard/components/view-selectors/factors"
import { useFactorsViewData } from "connected-components/factors-view/hooks/use-factors-view-data"
import { useSyncFactorsViewPreferences } from "connected-components/dashboard/hooks/use-sync-factors-view-preferences"
import { DatagridPersistenceContext } from "renderers/contributors-datagrid/hooks/use-datagrid-persistence/datagrid-persistence-context"
import { useAnalytics } from "connected-components/analytics"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import { FactorsViewAnalyticsProvider, useFactorsViewLoadedTracking, FACTORS_VIEW_EVENTS } from "./analytics"
import riskTemplate from "./templates/risk"
import performanceTemplate from "./templates/performance"
import contentSetTemplate from "./templates/content-set"
import betaTemplate from "./templates/beta"
import { DRIFT_DATES_SHAPE } from "./prop-types"
import "./style.scss"

// Maps moduleConfig keys → slice state keys for the fields seeded via
// initFromModuleConfig. The legacy "factors-view" fallback must skip the seeded
// keys so it does not clobber e.g. Thematic Betas' content-set initialView.
function getSeededFallbackKeys(moduleConfig) {
  if (moduleConfig == null) return []
  return compact([
    moduleConfig.initialView != null && "view",
    moduleConfig.sortMetricId != null && "sortMetricId",
    moduleConfig.selectedTableLevels != null && "selectedTableLevels",
  ])
}

export default function FactorsViewWrapper(props) {
  const { moduleConfig } = props
  const omitFallbackKeys = useMemo(() => getSeededFallbackKeys(moduleConfig), [moduleConfig])
  // Call before the sync hook: seed effect registers first (hook-call order), so
  // initFromModuleConfig lands before the legacy-fallback load. Async (effect), not sync.
  const factorsViewData = useFactorsViewData(moduleConfig)
  const isFactorsViewPreferencesLoaded = useSyncFactorsViewPreferences({
    omitFallbackKeys,
    duplicateFromPanelId: moduleConfig?.duplicateFromPanelId,
  })

  // Route ContributorsDataGrid persistence to the factors slice (not asset-view).
  const datagridPersistenceSource = useMemo(
    () => ({ datagridState: factorsViewData.datagridState, setDatagridState: factorsViewData.setDatagridState }),
    [factorsViewData.datagridState, factorsViewData.setDatagridState],
  )

  if (!isFactorsViewPreferencesLoaded) {
    return <Loading />
  }

  return <DatagridPersistenceContext.Provider value={datagridPersistenceSource}>
    <FactorsViewAnalyticsProvider>
      <FactorsView {...props} />
    </FactorsViewAnalyticsProvider>
  </DatagridPersistenceContext.Provider>
}

FactorsViewWrapper.propTypes = {
  moduleConfig: PropTypes.object,
}

function makeViewTemplates(contentSets) {
  const contentSetTemplates = chain(contentSets)
    .keyBy("id")
    .mapValues(() => contentSetTemplate)
    .value()

  return {
    risk: riskTemplate,
    performance: performanceTemplate,
    beta: betaTemplate,
    ...contentSetTemplates,
  }
}

function FactorsView(props) {
  const { instrumentsWithMetadata, contentSets } = props
  const { modelId, endDate, view, customViewMetricsStatus, selectedMode, instrument: original, comparisonInstrument: comparison, secondInstrument: rebalanced, filter, normalization, isExpanded } = useFactorsViewData()
  const [modelMetadata] = useModelMetadata(modelId)

  const instrumentOptions = { filter, normalization, isExpanded }
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison }, selectedMode, instrumentOptions)
  const instrumentColumns = useMemo(() => getOrderedInstrumentColumns(selectedMode), [selectedMode])
  const viewTemplates = useMemo(() => makeViewTemplates(contentSets), [contentSets])
  const [boundaryEarliestDate] = useInstrumentsEarliestAndLatestDates(modelMetadata, instrumentsWithMetadata)
  const driftDates = getDriftStartAndEndDates(endDate, boundaryEarliestDate)
  const selectedContentSetMetadata = useMemo(() => contentSets.find(contentSet => contentSet.id === view), [contentSets, view])

  const analytics = useAnalytics()
  useFactorsViewLoadedTracking()

  const handleViewSelected = useCallback((viewName) => {
    analytics.track(FACTORS_VIEW_EVENTS.VIEW_SELECTED, { viewName })
  }, [analytics])

  if (customViewMetricsStatus === LOADING || !endDate || !modelId) {
    return <Loading />
  }
  if (customViewMetricsStatus === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, {
      errorType: "data_loading_error",
      errorMessage: "Failed to load factors view data",
    })
    return <Error />
  }

  const template = viewTemplates[view] || viewTemplates.performance

  // TODO: [APP-1566] We probably want to adjust the start date to something that's meaningful for calculating drifts
  const content = <Box className="factors-view">
    <Paper className="factors-view-paper" square elevation={0}>
      <FactorsViewSelector
        contentSets={contentSets}
        onViewSelected={handleViewSelected}
      />
      <Box sx={{ minWidth: "3rem" }} />
      {template.Header && <template.Header contentSets={contentSets} view={view} driftDates={driftDates} />}
    </Paper>
    <template.Component
      instrumentsWithMetadata={instrumentsWithMetadata}
      instrumentsWithQueryOptions={instrumentsWithQueryOptions}
      instrumentColumns={instrumentColumns}
      selectedContentSetMetadata={selectedContentSetMetadata}
      view={view}
      driftDates={driftDates}
      modelMetadata={modelMetadata}
      {...props}
    />
  </Box>

  return content
}

FactorsView.propTypes = {
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
  contentSets: PropTypes.arrayOf(CONTENT_SET_METADATA).isRequired,
  driftDates: DRIFT_DATES_SHAPE.isRequired,
}
